import Emittery from 'emittery';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { UserHooksHandlers, HookPayloadsMap } from './hooks.js';
import { UserEventWrapper } from './events-wrapper.js';
import {
  assertPluginHooksShape,
  assertPluginName,
  assertPluginsProp,
  assertUserHookHandler,
  normalizePluginName,
} from './assertions.js';
import type { EventsMap } from './events.js';

export interface PluginsInterface {
  // Hooks events - called internally. The emitted payload does not include the
  // per-plugin `emitter`; that is injected by the hook handlers below so it can
  // carry the originating plugin name.
  hooksEmitter: Emittery<HookPayloadsMap>;
  // All available events for internal usage.
  // This one will be called from the internal code of the Wix CLI
  eventsEmitter: Emittery<EventsMap>;
  // Error handling during plugin code execution: hooks or events.
  onPluginError?: (
    error: unknown,
    context: {
      pluginName: string;
      handlerName: string;
    }
  ) => void;
}

function registerCliSetupHook(
  pluginsInterface: PluginsInterface,
  pluginName: string,
  hook: 'cli:setup',
  handler: NonNullable<UserHooksHandlers['cli:setup']>
) {
  pluginsInterface.hooksEmitter.on(hook, async (data) => {
    try {
      await handler({
        ...data,
        emitter: new UserEventWrapper(pluginsInterface, pluginName),
      });
    } catch (e) {
      pluginsInterface.onPluginError?.(e, {
        handlerName: hook,
        pluginName,
      });
    }
  });
}

export function createPluginsInterface(plugins: unknown): PluginsInterface {
  assertPluginsProp(plugins);

  const pluginsInterface: PluginsInterface = {
    hooksEmitter: new Emittery<HookPayloadsMap>(),
    eventsEmitter: new Emittery<EventsMap>(),
  };

  const seenPluginNameIndexes = new Map<string, number>();

  for (const [index, plugin] of (plugins ?? []).entries()) {
    assertPluginName(plugin, index);

    const normalizedName = normalizePluginName(plugin.name);
    const firstIndex = seenPluginNameIndexes.get(normalizedName);
    if (firstIndex !== undefined) {
      throw new CliError({
        code: CliErrorCode.PluginDuplicateName({
          name: normalizedName,
          firstIndex,
          duplicateIndex: index,
        }),
        cause: null,
      });
    }
    seenPluginNameIndexes.set(normalizedName, index);
    assertPluginHooksShape(plugin, plugin.name);

    const pluginHooks = plugin.hooks;
    if (pluginHooks === undefined) {
      continue;
    }

    for (const entry of Object.entries(pluginHooks)) {
      assertUserHookHandler(entry, plugin.name);

      const [hook, handler] = entry;
      registerCliSetupHook(pluginsInterface, plugin.name, hook, handler);
    }
  }

  return pluginsInterface;
}
